import React, { useContext, useEffect, useState } from 'react';
import { Button, CircularProgress, Modal } from '@material-ui/core';

import { inboundTruckApi as api } from 'api/inboundTrucks';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

function POListModal({ facilityID, onClose, setNotification, truckID }) {
    const [loading, setLoading] = useState(true);
    const [poList, setPoList] = useState([]);

    useEffect(() => {
        const fetchPOs = async () => {
            try {
                const response = await api.getTruckDetails(truckID, facilityID);
                if (response.status === false) {
                    setNotification({
                        text: `Failed to fetch POs: ${response.msg}`,
                        level: 'error',
                    });
                    return;
                }
                setPoList(response.data.output.pos);
                setLoading(false);
            } catch (err) {
                setNotification({
                    text: `Error fetching POs: ${err.message}`,
                    level: 'error',
                });
                setLoading(false);
            }
        };
        fetchPOs();
    }, [truckID, facilityID]);

    return (
        <Modal open onClose={onClose}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '500px', margin: '10% auto', padding: '20px' }}>
                <h2>POs on Truck {truckID}</h2>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {poList.map((po) => (
                            <li key={po.id} style={{ padding: '8px 0', borderBottom: '1px solid #eee' }}>
                                PO {po.id} - {po.vendorName}
                            </li>
                        ))}
                    </ul>
                )}
                <Button variant="contained" onClick={onClose} style={{ marginTop: '20px' }}>
                    Close
                </Button>
            </div>
        </Modal>
    );
}

function TruckStopEvents() {
    const [truckStopEvents, setTruckStopEvents] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [poListModalOpen, setPoListModalOpen] = useState(false);
    const [selectedTruckID, setSelectedTruckID] = useState(null);
    const [loading, setLoading] = useState(false);
    const user = useContext(UserContext);

    const fetchTruckStopEvents = async () => {
        const res = await api.getTruckStopEvents();

        if (!res.status) {
            setNotification({
                text: `Error fetching truck stop events: ${res.msg}`,
                level: 'error',
            });
            return;
        }

        const formattedEvents = res.data.rows.map((event) => ({
            ...event,
            editDate: event.editDate.date.split('.')[0],
        }));

        setTruckStopEvents(formattedEvents);
    };

    const handleExport = async () => {
        setLoading(true);
        try {
            await api.exportTruckStopEvents();
        } catch (err) {
            setNotification({
                text: `Error exporting truck stop events: ${err.message}`,
                level: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            Header: 'Truck ID',
            accessor: 'truckID',
        },
        {
            Header: 'View POs',
            Cell: ({ row }) => (
                <Button
                    variant="contained"
                    onClick={() => {
                        setSelectedTruckID(row.original.truckID);
                        setPoListModalOpen(true);
                    }}
                >
                    View POs
                </Button>
            ),
        },
        {
            Header: 'Date of Event',
            accessor: 'editDate',
        },
        {
            Header: 'Reason Code',
            accessor: 'reasonCode',
        },
        {
            Header: 'Created By',
            accessor: 'createdBy',
        },
    ];

    useEffect(() => {
        fetchTruckStopEvents();
    }, []);

    const toolbarActions = [
        {
            name: 'Export',
            action: handleExport,
        },
    ];

    return (
        <div>
            <Notifications options={notification} />
            <DataTable data={truckStopEvents} columns={columns} toolbarActions={toolbarActions} />
            {poListModalOpen && (
                <POListModal
                    truckID={selectedTruckID}
                    setNotification={setNotification}
                    facilityID={user.getFacilityID()}
                    onClose={() => {
                        setPoListModalOpen(false);
                        setSelectedTruckID(null);
                    }}
                />
            )}
        </div>
    );
}

export { TruckStopEvents };
